import { useCallback, useRef, type FocusEvent, type KeyboardEvent } from "react";
import type { ImpersonatableUser } from "@gui/infrastructure/pass-through/api";

interface ImpersonationMenuSectionProps {
	onImpersonationPicked: (picked: ImpersonatableUser | null) => void;
	stopImpersonation: () => void;
	onClose: () => void;
}

export function useImpersonationMenuSectionViewModel(props: ImpersonationMenuSectionProps) {
	const { onImpersonationPicked, stopImpersonation, onClose } = props;
	const pickerInputRef = useRef<HTMLInputElement>(null);

	// Switching user refetches the whole session, so the menu has nothing left to offer.
	const handlePicked = useCallback(
		(picked: ImpersonatableUser | null) => {
			onImpersonationPicked(picked);
			onClose();
		},
		[onImpersonationPicked, onClose],
	);

	const handleStop = useCallback(() => {
		stopImpersonation();
		onClose();
	}, [stopImpersonation, onClose]);

	// MenuList moves focus only to elements with a tabindex, so the wrapper is the picker's stop in
	// the menu's arrow-key order; it hands that focus straight on to the input.
	const handleWrapperFocus = useCallback((event: FocusEvent<HTMLElement>) => {
		if (event.target === event.currentTarget) pickerInputRef.current?.focus();
	}, []);

	// Keep typing and arrow keys from MenuList, which would jump to the item starting with that
	// letter. Escape (once the list is closed) and Tab still reach Menu, which closes on them.
	const handleWrapperKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
		if (event.key !== "Escape" && event.key !== "Tab") event.stopPropagation();
	}, []);

	return { pickerInputRef, handlePicked, handleStop, handleWrapperFocus, handleWrapperKeyDown };
}
